import React, { useCallback, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import Navbar from 'components/navbar';
import Card from 'components/card';
import Loader from 'components/loader/loader';
import customAxios from 'services/appservices';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
    MdOutlineMyLocation,
    MdOutlineExplore,
    MdOutlineRadar,
} from 'react-icons/md';

const COVERAGE_STYLES = {
    fillColor: 'rgba(34, 197, 94, 0.15)',
    strokeColor: 'rgba(34, 197, 94, 0.6)',
    strokeWidth: 2,
};

function MapRecenter({
    center,
    zoom,
}: {
    center: { lat: number; lng: number };
    zoom: number;
}): JSX.Element | null {
    const map = useMap();
    useEffect(() => {
        map.setView([center.lat, center.lng], zoom);
    }, [center.lat, center.lng, zoom, map]);
    return null;
}

interface CoverageConfig {
    lat: number;
    lng: number;
    radiusMeters: number;
}

function CoverageArea() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<CoverageConfig | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadCoverageConfig = useCallback(async () => {
        setLoading(true);
        try {
            const res = await customAxios.get('/coverage-area');
            if (res?.data) {
                setConfig(res.data);
            }
        } catch (err: any) {
            console.error('Error loading coverage area:', err);
            setError('Não foi possível carregar a área de cobertura');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCoverageConfig();
    }, [loadCoverageConfig]);

    const getZoomFromRadius = (radiusMeters: number): number => {
        if (radiusMeters <= 5000) return 13;
        if (radiusMeters <= 10000) return 12;
        if (radiusMeters <= 20000) return 11;
        if (radiusMeters <= 40000) return 10;
        return 9;
    };

    if (loading) {
        return (
            <div>
                <Navbar flag={false} brandText={t('Coverage Area')} />
                <Loader />
            </div>
        );
    }

    if (error || !config) {
        return (
            <div>
                <Navbar flag={false} brandText={t('Coverage Area')} />
                <Link
                    to="/admin/settings"
                    className="mt-3 mb-1 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                    <FaArrowLeft className="h-3 w-3" />
                    {t("Voltar para Configurações")}
                </Link>
                <Card extra="w-full mt-2 pb-6 p-6 h-full">
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10 mb-4">
                            <MdOutlineRadar className="h-8 w-8 text-red-400" />
                        </div>
                        <p className="text-lg font-semibold text-white mb-2">
                            Área de Cobertura Não Configurada
                        </p>
                        <p className="text-sm text-gray-400 text-center max-w-md">
                            {error || 'Configure as variáveis de ambiente no backend para definir a área de operação.'}
                        </p>
                        <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-gray-400 font-mono">
                            <div><span className="text-cyan-400">COVERAGE_CENTER_LAT</span>="-23.5505"</div>
                            <div><span className="text-cyan-400">COVERAGE_CENTER_LNG</span>="-46.6333"</div>
                            <div><span className="text-cyan-400">COVERAGE_RADIUS_METERS</span>="30000"</div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    const zoom = getZoomFromRadius(config.radiusMeters);
    const radiusKm = (config.radiusMeters / 1000).toFixed(1);

    const infoCards = [
        {
            label: t('Latitude'),
            value: config.lat.toFixed(7),
            icon: <MdOutlineMyLocation className="h-5 w-5" />,
            color: "text-cyan-400",
            iconBg: "bg-cyan-500/15 text-cyan-400",
        },
        {
            label: t('Longitude'),
            value: config.lng.toFixed(7),
            icon: <MdOutlineExplore className="h-5 w-5" />,
            color: "text-purple-400",
            iconBg: "bg-purple-500/15 text-purple-400",
        },
        {
            label: t('Raio'),
            value: `${radiusKm} km`,
            extra: `(${config.radiusMeters.toLocaleString()} m)`,
            icon: <MdOutlineRadar className="h-5 w-5" />,
            color: "text-green-400",
            iconBg: "bg-green-500/15 text-green-400",
        },
    ];

    return (
        <div>
            <Navbar flag={false} brandText={t('Coverage Area')} />
            <Link
                to="/admin/settings"
                className="mt-3 mb-1 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
                <FaArrowLeft className="h-3 w-3" />
                {t("Voltar para Configurações")}
            </Link>

            <Card extra="w-full mt-2 pb-6 p-6 h-full">
                <header className="mb-6">
                    <h2 className="text-2xl font-bold text-white">
                        {t('Coverage Area')}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                        {t('Área de operação da plataforma definida pelo administrador do sistema.')}
                    </p>
                </header>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {infoCards.map((card) => (
                        <div key={card.label} className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                            <div className="flex items-center gap-3 mb-2">
                                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${card.iconBg}`}>
                                    {card.icon}
                                </div>
                                <p className="text-xs text-gray-400 uppercase tracking-wider">
                                    {card.label}
                                </p>
                            </div>
                            <p className={`text-xl font-bold ${card.color} font-mono`}>
                                {card.value}{' '}
                                {card.extra && (
                                    <span className="text-sm font-normal text-gray-400">
                                        {card.extra}
                                    </span>
                                )}
                            </p>
                        </div>
                    ))}
                </div>

                {/* .env Notice */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">ⓘ</span>
                    <p className="text-sm text-amber-300/80">
                        Esta configuração é definida via variáveis de ambiente (
                        <code className="text-amber-400 font-mono text-xs">
                            COVERAGE_CENTER_LAT, COVERAGE_CENTER_LNG, COVERAGE_RADIUS_METERS
                        </code>
                        ) no arquivo <code className="text-amber-400 font-mono text-xs">.env</code> do backend.
                    </p>
                </div>

                {/* Map */}
                <div
                    className="rounded-2xl overflow-hidden border border-white/10"
                    style={{ height: '500px' }}
                >
                    <MapContainer
                        center={[config.lat, config.lng]}
                        zoom={zoom}
                        style={{ width: '100%', height: '100%' }}
                        zoomControl={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                        />
                        <MapRecenter center={config} zoom={zoom} />

                        <Circle
                            center={[config.lat, config.lng]}
                            radius={config.radiusMeters}
                            pathOptions={{
                                color: COVERAGE_STYLES.strokeColor,
                                fillColor: COVERAGE_STYLES.fillColor,
                                fillOpacity: 0.4,
                                weight: COVERAGE_STYLES.strokeWidth,
                            }}
                        />
                        <Circle
                            center={[config.lat, config.lng]}
                            radius={120}
                            pathOptions={{
                                color: '#00C2FF',
                                fillColor: '#00C2FF',
                                fillOpacity: 0.8,
                                weight: 2,
                            }}
                        />
                    </MapContainer>
                </div>
            </Card>
        </div>
    );
}

export default CoverageArea;
